import { LearnArticleType } from '@cibo/core'
import { AuthUserPermission, Can } from '@cibo/profile'
import { ClickableCard, ResponsiveDialog, Scrim } from '@cibo/ui'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { BLOCKS } from '@contentful/rich-text-types'
import {
  Box,
  Button,
  CardContent,
  CardMedia,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface LearnArticleCardProps {
  entry: LearnArticleType
}

export const LearnArticleCard = ({
  entry: {
    fields: { body, headline, lead, teaserImage },
  },
}: LearnArticleCardProps) => {
  const { t } = useTranslation('@cibo/ui')
  const [open, setOpen] = useState(false)
  const doc = documentToReactComponents(body, {
    renderNode: {
      [BLOCKS.EMBEDDED_ASSET]: ({
        data: {
          target: {
            fields: {
              alt,
              file: { url },
            },
          },
        },
      }) => (
        <Box sx={{ float: 'left', mt: 2, mr: 3 }}>
          <img alt={alt} src={`${url}?w=150&h=150`} />
        </Box>
      ),
      [BLOCKS.EMBEDDED_ENTRY]: ({
        data: {
          target: {
            fields,
            sys: {
              contentType: {
                sys: { id },
              },
            },
          },
        },
      }) => {
        if (id === 'youtubeEmbed') {
          return (
            <iframe
              width="560"
              height="315"
              src={`https://www.youtube.com/embed/${fields.youTubeId}`}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          )
        }
        return null
      },
    },
  })

  return (
    <Can useAny={AuthUserPermission.LEARN_VIEW}>
      <ClickableCard elevation={2} onClick={() => setOpen(true)} sx={{ flex: 1 }}>
        <CardMedia
          image={`${teaserImage.fields.file.url}?h=250&fm=jpg&q=50`}
          sx={{ minHeight: 150, position: 'relative', display: 'flex' }}
        >
          <Scrim />
          <Box
            p={2}
            zIndex={1}
            position="relative"
            flex={1}
            display="flex"
            flexDirection="column"
            justifyContent="flex-end"
            data-testid="learnBox"
          >
            <Typography variant="h5" color="primary.contrastText">
              {headline}
            </Typography>
          </Box>
        </CardMedia>
        <CardContent>
          <Typography>{lead}</Typography>
        </CardContent>
      </ClickableCard>
      <ResponsiveDialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{headline}</DialogTitle>
        <DialogContent>{doc}</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('close')}</Button>
        </DialogActions>
      </ResponsiveDialog>
    </Can>
  )
}
